import { useMemo } from "react"

import { useQuery } from "@apollo/client"

import { getFragmentData } from "@/main/gql/__generated__"
import { graphql } from "@/main/gql/__generated__/gql"
import type {
	CancelType,
	CreateCancelCancelTypeInput,
	Role,
} from "@/main/gql/__generated__/graphql"
import { useStoreManagementQuery } from "@/provider/apollo/Apollo"

// ── 公開型定義 ──────────────────────────────────────────

export type SalonForSelect = {
	readonly id: number
	readonly name: string
	readonly displayName?: string | null
}

export type MeCouponMaster = {
	readonly id: string
	readonly name: string
}

export type MeUserPermission = {
	readonly canUpdateSalon: boolean
	readonly canUpsertClaimRule: boolean
	readonly canCreateCouponMaster: boolean
	readonly canUpdateSalonUserRole: boolean
	readonly canSwitchSalonOwner: boolean
	readonly isSouhlAdmin: boolean
	readonly canInviteSalonUser: boolean
	readonly canDeleteSalonUser: boolean
	readonly canActivateCouponMaster: boolean
	readonly canDeactivateCouponMaster: boolean
	readonly canUpdateDefaultCouponMasterSetting: boolean
}

export type MeFeatureFlag = {
	readonly salonId: number
	readonly isEncouragingRereservationEnabled: boolean
	readonly forceEmptyWhenCannotReserveCanceledReservationMenuSet: boolean
}

export type MeSalon = {
	readonly id: number
	readonly name: string
	readonly displayName: string
	readonly address: string
	readonly canEnqueueSyncCancels: boolean
	readonly enableDefaultCouponMasterForNoConfirmationCancel: boolean
	readonly selectableCancelTypes: CancelType[]
	readonly selectableCancelTypesWithoutUnspecifiedOnes: CancelType[]
	readonly defaultSelectedCancelTypeForCreatingCancel: CreateCancelCancelTypeInput
	readonly lapsedAfterCancelMinDays: number
	readonly defaultCouponMaster: MeCouponMaster | null
	readonly userPermission: MeUserPermission
	readonly featureFlag: MeFeatureFlag
}

export type MeSalonUser = {
	readonly id: string
	readonly email: string
	readonly role: Role
	readonly lastLoginTime: string | null
	readonly createTime: string
}

export type MeSalonDetail = {
	readonly id: number
	readonly isReservationScrapingActive: boolean
}

// ── Fragment 定義（非公開） ─────────────────────────────

const couponMasterNameFragment = graphql(/* GraphQL */ `
	fragment couponMasterName on CouponMaster {
		id
		name
	}
`)

const useUserPermissionFragment = graphql(/* GraphQL */ `
	fragment useUserPermission on UserPermission {
		canUpdateSalon
		canUpsertClaimRule
		canCreateCouponMaster
		canUpdateSalonUserRole
		canSwitchSalonOwner
		isSouhlAdmin
		canInviteSalonUser
		canDeleteSalonUser
		canActivateCouponMaster
		canDeactivateCouponMaster
		canUpdateDefaultCouponMasterSetting
	}
`)

const useFeatureFlagFragment = graphql(/* GraphQL */ `
	fragment useFeatureFlag on FeatureFlag {
		salonId
		isEncouragingRereservationEnabled
		forceEmptyWhenCannotReserveCanceledReservationMenuSet
	}
`)

const salonAllFieldFragment = graphql(/* GraphQL */ `
	fragment salonAllField on Salon {
		id
		name
		displayName
		address
		canEnqueueSyncCancels
		enableDefaultCouponMasterForNoConfirmationCancel
		selectableCancelTypes
		selectableCancelTypesWithoutUnspecifiedOnes
		defaultSelectedCancelTypeForCreatingCancel
		lapsedAfterCancelMinDays
		defaultCouponMaster {
			...couponMasterName
		}
		userPermission {
			...useUserPermission
		}
		featureFlag {
			...useFeatureFlag
		}
	}
`)

const salonUserAllFieldFragment = graphql(/* GraphQL */ `
	fragment salonUserAllField on SalonUser {
		id
		email
		role
		lastLoginTime
		createTime
	}
`)

const salonNameFragment = graphql(/* GraphQL */ `
	fragment salonName on Salon {
		id
		name
		displayName
	}
`)

const salonDetailFragment = graphql(/* GraphQL */ `
	fragment salonDetailField on SalonDetail {
		id
		isReservationScrapingActive
	}
`)

const getMeQuery = graphql(/* GraphQL */ `
	query GetMe {
		me {
			salon {
				...salonAllField
			}
			salonDetail {
				...salonDetailField
			}
			salons {
				...salonName
			}
			salonUser {
				...salonUserAllField
			}
		}
	}
`)

// ── フック（公開 API） ─────────────────────────────────

type UseMeReturn = {
	loading: boolean
	refetch: () => void
	salon?: MeSalon
	salonDetail?: MeSalonDetail
	salonUser?: MeSalonUser
	salons: readonly SalonForSelect[]
}

export const useMe = (): UseMeReturn => {
	const { data, refetch, loading } = useQuery(getMeQuery, {
		fetchPolicy: "cache-first",
	})

	const salon = useMemo((): MeSalon | undefined => {
		const rawSalon =
			getFragmentData(salonAllFieldFragment, data?.me.salon) ?? undefined
		if (!rawSalon) return undefined
		return {
			...rawSalon,
			defaultCouponMaster:
				getFragmentData(
					couponMasterNameFragment,
					rawSalon.defaultCouponMaster
				) ?? null,
			userPermission: getFragmentData(
				useUserPermissionFragment,
				rawSalon.userPermission
			),
			featureFlag: getFragmentData(
				useFeatureFlagFragment,
				rawSalon.featureFlag
			),
		}
	}, [data?.me.salon])

	const salonDetail = useMemo(
		() =>
			getFragmentData(salonDetailFragment, data?.me.salonDetail) ?? undefined,
		[data?.me.salonDetail]
	)

	const salons = useMemo(
		() => getFragmentData(salonNameFragment, data?.me.salons) ?? [],
		[data?.me.salons]
	)

	const salonUser = useMemo(
		() =>
			getFragmentData(salonUserAllFieldFragment, data?.me.salonUser) ??
			undefined,
		[data?.me.salonUser]
	)

	return { refetch, salon, salonDetail, salons, salonUser, loading }
}

/**
 * souhl_admin 用: store-management supergraph からサロン一覧を取得するフック。
 * fragment/query を内部に閉じ込め、unmask 済みデータを返す。
 */
export const useSalonListForStoreManagement = (): {
	salons: readonly SalonForSelect[]
	loading: boolean
} => {
	const { data, loading } = useStoreManagementQuery(getMeQuery, {
		fetchPolicy: "cache-first",
	})
	const salons = useMemo(
		() => getFragmentData(salonNameFragment, data?.me.salons) ?? [],
		[data?.me.salons]
	)
	return { salons, loading }
}
